import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PregnancyDocs.css';
import { useProfile } from '../../../context/ProfileContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faUpload,
  faClipboardList,
  faSuitcase,
  faTrash,
  faCheck,
  faSpinner,
  faTriangleExclamation,
  faBell,
  faPen,
  faFilePdf,
  faFileImage,
  faFile,
  faDownload,
  faNotesMedical,
  faSquareCheck,
  faSquare,
  faHospital,
  faUserDoctor,
  faPhone,
  faStar,
  faBaby,
  faShirt,
  faHandHoldingHeart,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Container } from 'react-bootstrap';
import BackButton from '../../../components/BackButton/BackButton';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import ProfileStrip from '../../../components/ProfileStrip/ProfileStrip';
import womensHealthService, {
  PregnancyProfile,
  PregnancyDocument,
  BirthPlan,
  HospitalBagItem,
  HospitalBagColumn,
} from '../../../services/womensHealthService';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

type TabKey = 'documents' | 'birth-plan' | 'hospital-bag';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ChoiceOption {
  value: string;
  display_name: string;
}

/* ================================================================== */
/*  Toast                                                              */
/* ================================================================== */

const Toast: React.FC<{ message: string; type: string; onClose: () => void }> = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  const icons: Record<string, IconDefinition> = { success: faCheck, error: faTriangleExclamation, info: faBell };
  return (
    <div className={`wh-pdoc-toast wh-pdoc-toast-${type}`}>
      <span className="wh-pdoc-toast-icon"><FontAwesomeIcon icon={icons[type] || faBell} /></span>
      <span className="wh-pdoc-toast-msg">{message}</span>
      <button className="wh-pdoc-toast-close" onClick={onClose}>{'\u00D7'}</button>
    </div>
  );
};

/* ================================================================== */
/*  Hospital Bag Title -> Icon & Color Mapping                         */
/* ================================================================== */
const BAG_STYLES: Record<string, { icon: IconDefinition; color: string }> = {
  'For Mom': { icon: faHandHoldingHeart, color: '#ec4899' },
  'For Baby': { icon: faBaby, color: '#8b5cf6' },
  'For Partner': { icon: faShirt, color: '#06b6d4' },
};
const resolveBagStyle = (title: string): { icon: IconDefinition; color: string } =>
  BAG_STYLES[title] || { icon: faSuitcase, color: '#64748b' };

/* ================================================================== */
/*  Birth Plan Initial Form                                            */
/* ================================================================== */

const BIRTH_PLAN_INIT = {
  delivery_preference: '',
  pain_management: '',
  hospital_name: '',
  doctor_name: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  special_requests: '',
  notes: '',
};

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string): string => {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getFileIcon = (url: string): IconDefinition => {
  if (!url) return faFile;
  const lower = url.toLowerCase();
  if (lower.endsWith('.pdf')) return faFilePdf;
  if (lower.match(/\.(jpg|jpeg|png|gif|webp)$/)) return faFileImage;
  return faFile;
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const PregnancyDocs: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  const [pregnancy, setPregnancy] = useState<PregnancyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'documents');
  const [toast, setToast] = useState<ToastState | null>(null);

  // Documents state
  const [documents, setDocuments] = useState<PregnancyDocument[]>([]);
  const [docForm, setDocForm] = useState({ document_type: '', title: '', notes: '' });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<number | null>(null);

  // Birth Plan state
  const [birthPlan, setBirthPlan] = useState<BirthPlan | null>(null);
  const [bpForm, setBpForm] = useState(BIRTH_PLAN_INIT);
  const [bpChoices, setBpChoices] = useState<{ delivery_preference: ChoiceOption[]; pain_management: ChoiceOption[] }>({ delivery_preference: [], pain_management: [] });
  const [savingBp, setSavingBp] = useState(false);
  const [editingBp, setEditingBp] = useState(false);

  // Hospital Bag state
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [bagColumns, setBagColumns] = useState<HospitalBagColumn[]>([]);

  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Load Data                                                        */
  /* ---------------------------------------------------------------- */

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const active = await womensHealthService.getActivePregnancy();
      setPregnancy(active);

      if (active) {
        const [docs, bp, bpCh, bag] = await Promise.all([
          womensHealthService.getPregnancyDocuments(active.id),
          womensHealthService.getBirthPlans(active.id),
          womensHealthService.getBirthPlanChoices(),
          womensHealthService.getHospitalBag(active.id),
        ]);
        setDocuments(Array.isArray(docs) ? docs : []);
        if (Array.isArray(bp) && bp.length > 0) {
          setBirthPlan(bp[0]);
          setBpForm({
            delivery_preference: bp[0].delivery_preference || '',
            pain_management: bp[0].pain_management || '',
            hospital_name: bp[0].hospital_name || '',
            doctor_name: bp[0].doctor_name || '',
            emergency_contact_name: bp[0].emergency_contact_name || '',
            emergency_contact_phone: bp[0].emergency_contact_phone || '',
            special_requests: bp[0].special_requests || '',
            notes: bp[0].notes || '',
          });
        }
        if (bpCh) {
          setBpChoices({
            delivery_preference: bpCh.delivery_preference || [],
            pain_management: bpCh.pain_management || [],
          });
        }
        if (Array.isArray(bag)) {
          const next: Record<string, boolean> = {};
          bag.forEach((row: HospitalBagItem) => { next[row.item_key] = row.checked; });
          setCheckedItems(next);
        }
      }
    } catch (err) {
      console.error('Failed to load pregnancy data:', err);
      showToast('Failed to load data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  useEffect(() => {
    let cancelled = false;
    womensHealthService.getHospitalBagDefaults()
      .then((cols) => { if (!cancelled) setBagColumns(cols); })
      .catch((err) => { console.error('Failed to load hospital bag defaults:', err); });
    return () => { cancelled = true; };
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Document Handlers                                                */
  /* ---------------------------------------------------------------- */

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast('Please select a file.', 'error');
      return;
    }
    if (!docForm.title.trim()) {
      showToast('Please enter a document title.', 'error');
      return;
    }

    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', selectedFile);
      fd.append('title', docForm.title.trim());
      fd.append('document_type', docForm.document_type || 'other');
      fd.append('notes', docForm.notes.trim());
      if (pregnancy) fd.append('pregnancy', String(pregnancy.id));

      await womensHealthService.uploadPregnancyDocument(fd);
      showToast('Document uploaded successfully!', 'success');
      setDocForm({ document_type: '', title: '', notes: '' });
      setSelectedFile(null);
      const fileInput = document.getElementById('wh-pdoc-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      await fetchData();
    } catch (err) {
      console.error('Upload failed:', err);
      showToast('Failed to upload document.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (id: number) => {
    try {
      await womensHealthService.deletePregnancyDocument(id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      showToast('Document deleted.', 'success');
      setConfirmDeleteDoc(null);
    } catch {
      showToast('Failed to delete document.', 'error');
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Birth Plan Handlers                                              */
  /* ---------------------------------------------------------------- */

  const handleSaveBirthPlan = async () => {
    try {
      setSavingBp(true);
      const payload = { ...bpForm, pregnancy: pregnancy?.id };

      if (birthPlan) {
        const updated = await womensHealthService.updateBirthPlan(birthPlan.id, payload);
        setBirthPlan(updated);
        showToast('Birth plan updated!', 'success');
      } else {
        const created = await womensHealthService.createBirthPlan(payload);
        setBirthPlan(created);
        showToast('Birth plan created!', 'success');
      }
      setEditingBp(false);
    } catch (err) {
      console.error('Save birth plan failed:', err);
      showToast('Failed to save birth plan.', 'error');
    } finally {
      setSavingBp(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Hospital Bag Toggle                                              */
  /* ---------------------------------------------------------------- */

  const toggleItem = (key: string) => {
    setCheckedItems((prev) => {
      const nextValue = !prev[key];
      if (pregnancy) {
        womensHealthService
          .saveHospitalBagItem({ pregnancy: pregnancy.id, item_key: key, checked: nextValue })
          .catch((err) => {
            console.error('Failed to save hospital bag item:', err);
            showToast('Failed to save checklist update.', 'error');
          });
      }
      return { ...prev, [key]: nextValue };
    });
  };

  const totalBagItems = bagColumns.reduce((sum, col) => sum + col.items.length, 0);
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;

  /* ---------------------------------------------------------------- */
  /*  Tabs                                                             */
  /* ---------------------------------------------------------------- */

  const tabs: { key: TabKey; label: string; icon: IconDefinition }[] = [
    { key: 'documents', label: 'Documents', icon: faFileLines },
    { key: 'birth-plan', label: 'Birth Plan', icon: faClipboardList },
    { key: 'hospital-bag', label: 'Hospital Bag', icon: faSuitcase },
  ];

  /* ---------------------------------------------------------------- */
  /*  Render: Loading / No Pregnancy                                   */
  /* ---------------------------------------------------------------- */

  if (loading) {
    return (
      <div className="wh-pdoc-page">
        <section className="wh-pdoc-hero">
          <Container>
            <BackButton />
            <div className="wh-pdoc-hero-content">
              <div className="wh-pdoc-hero-badge"><FontAwesomeIcon icon={faFileLines} /> Pregnancy</div>
              <h1 className="wh-pdoc-hero-title">Documents & Preparation</h1>
            </div>
          </Container>
        </section>
        <div className="wh-pdoc-loading">
          <FontAwesomeIcon icon={faSpinner} spin className="wh-pdoc-loading-icon" />
          <p>Loading pregnancy data...</p>
        </div>
      </div>
    );
  }

  if (!pregnancy) {
    return (
      <div className="wh-pdoc-page">
        <section className="wh-pdoc-hero">
          <Container>
            <BackButton />
            <div className="wh-pdoc-hero-content">
              <div className="wh-pdoc-hero-badge"><FontAwesomeIcon icon={faFileLines} /> Pregnancy</div>
              <h1 className="wh-pdoc-hero-title">{t('womensHealth:pregnancyDocs.title')}</h1>
            </div>
          </Container>
        </section>
        <Container>
          <div className="wh-pdoc-empty-state">
            <div className="wh-pdoc-empty-icon"><FontAwesomeIcon icon={faFileLines} /></div>
            <h3>No Active Pregnancy</h3>
            <p>Please set up your pregnancy profile first to access documents, birth plan, and hospital bag checklist.</p>
            <a href="/womens-health/pregnancy" className="wh-pdoc-btn wh-pdoc-btn-primary">
              <FontAwesomeIcon icon={faBaby} /> Go to Pregnancy Dashboard
            </a>
          </div>
        </Container>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Render: Documents Tab                                            */
  /* ---------------------------------------------------------------- */

  const renderDocuments = () => (
    <div className="wh-pdoc-documents">
      <div className="wh-pdoc-upload-card">
        <div className="wh-pdoc-card-header">
          <FontAwesomeIcon icon={faUpload} />
          <h3>{t('womensHealth:pregnancyDocs.uploadDoc')}</h3>
        </div>
        <div className="wh-pdoc-card-body">
          <div className="wh-pdoc-form-row">
            <div className="wh-pdoc-form-group">
              <label className="wh-pdoc-label">Document Title <span className="wh-pdoc-required">*</span></label>
              <input
                type="text"
                className="wh-pdoc-input"
                placeholder="e.g., Ultrasound Week 20"
                value={docForm.title}
                onChange={(e) => setDocForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="wh-pdoc-form-group">
              <label className="wh-pdoc-label">Document Type</label>
              <select
                className="wh-pdoc-select"
                value={docForm.document_type}
                onChange={(e) => setDocForm((f) => ({ ...f, document_type: e.target.value }))}
              >
                <option value="">Select type</option>
                <option value="ultrasound">Ultrasound</option>
                <option value="lab_result">Lab Result</option>
                <option value="prescription">Prescription</option>
                <option value="report">Medical Report</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="wh-pdoc-form-group">
            <label className="wh-pdoc-label">File <span className="wh-pdoc-required">*</span></label>
            <div className="wh-pdoc-file-input-wrapper">
              <input
                type="file"
                id="wh-pdoc-file-input"
                className="wh-pdoc-file-input"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
              <label htmlFor="wh-pdoc-file-input" className="wh-pdoc-file-label">
                <FontAwesomeIcon icon={faUpload} />
                {selectedFile ? selectedFile.name : 'Choose file...'}
              </label>
            </div>
          </div>
          <div className="wh-pdoc-form-group">
            <label className="wh-pdoc-label">Notes</label>
            <textarea
              className="wh-pdoc-textarea"
              rows={2}
              placeholder="Optional notes about this document..."
              value={docForm.notes}
              onChange={(e) => setDocForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
          <button className="wh-pdoc-btn wh-pdoc-btn-primary" onClick={handleUpload} disabled={uploading}>
            {uploading ? <><FontAwesomeIcon icon={faSpinner} spin /> {t('common:buttons.upload')}...</> : <><FontAwesomeIcon icon={faUpload} /> {t('womensHealth:pregnancyDocs.uploadDoc')}</>}
          </button>
        </div>
      </div>

      <div className="wh-pdoc-list-section">
        <h3 className="wh-pdoc-list-title"><FontAwesomeIcon icon={faFileLines} /> Your Documents ({documents.length})</h3>
        {documents.length === 0 ? (
          <div className="wh-pdoc-list-empty">
            <p>No documents uploaded yet. Upload your ultrasound images, lab results, and reports above.</p>
          </div>
        ) : (
          <div className="wh-pdoc-doc-grid">
            {documents.map((doc) => (
              <div className="wh-pdoc-doc-card" key={doc.id}>
                <div className="wh-pdoc-doc-icon">
                  <FontAwesomeIcon icon={getFileIcon(doc.file)} />
                </div>
                <div className="wh-pdoc-doc-info">
                  <h4 className="wh-pdoc-doc-title">{doc.title}</h4>
                  <span className="wh-pdoc-doc-type">{doc.document_type_display || doc.document_type}</span>
                  <span className="wh-pdoc-doc-date">{formatDate(doc.created_at)}</span>
                  {doc.notes && <p className="wh-pdoc-doc-notes">{doc.notes}</p>}
                </div>
                <div className="wh-pdoc-doc-actions">
                  {doc.file && (
                    <a href={doc.file} target="_blank" rel="noopener noreferrer" className="wh-pdoc-action-btn wh-pdoc-action-view" title="View/Download">
                      <FontAwesomeIcon icon={faDownload} />
                    </a>
                  )}
                  <button className="wh-pdoc-action-btn wh-pdoc-action-delete" onClick={() => setConfirmDeleteDoc(doc.id)} title="Delete">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  /* ---------------------------------------------------------------- */
  /*  Render: Birth Plan Tab                                           */
  /* ---------------------------------------------------------------- */

  const renderBirthPlan = () => {
    const isEditing = editingBp || !birthPlan;

    return (
      <div className="wh-pdoc-birth-plan">
        {birthPlan && !editingBp && (
          <div className="wh-pdoc-bp-view">
            <div className="wh-pdoc-bp-header">
              <h3><FontAwesomeIcon icon={faClipboardList} /> Your Birth Plan</h3>
              <button className="wh-pdoc-btn wh-pdoc-btn-outline" onClick={() => setEditingBp(true)}>
                <FontAwesomeIcon icon={faPen} /> Edit
              </button>
            </div>
            <div className="wh-pdoc-bp-grid">
              <div className="wh-pdoc-bp-item">
                <span className="wh-pdoc-bp-label">Delivery Preference</span>
                <span className="wh-pdoc-bp-value">{birthPlan.delivery_preference_display || '--'}</span>
              </div>
              <div className="wh-pdoc-bp-item">
                <span className="wh-pdoc-bp-label">Pain Management</span>
                <span className="wh-pdoc-bp-value">{birthPlan.pain_management_display || '--'}</span>
              </div>
              <div className="wh-pdoc-bp-item">
                <span className="wh-pdoc-bp-label"><FontAwesomeIcon icon={faHospital} /> Hospital</span>
                <span className="wh-pdoc-bp-value">{birthPlan.hospital_name || '--'}</span>
              </div>
              <div className="wh-pdoc-bp-item">
                <span className="wh-pdoc-bp-label"><FontAwesomeIcon icon={faUserDoctor} /> Doctor</span>
                <span className="wh-pdoc-bp-value">{birthPlan.doctor_name || '--'}</span>
              </div>
              <div className="wh-pdoc-bp-item">
                <span className="wh-pdoc-bp-label"><FontAwesomeIcon icon={faPhone} /> Emergency Contact</span>
                <span className="wh-pdoc-bp-value">{birthPlan.emergency_contact_name || '--'} ({birthPlan.emergency_contact_phone || '--'})</span>
              </div>
            </div>
            {birthPlan.special_requests && (
              <div className="wh-pdoc-bp-section">
                <h4><FontAwesomeIcon icon={faStar} /> Special Requests</h4>
                <p>{birthPlan.special_requests}</p>
              </div>
            )}
            {birthPlan.notes && (
              <div className="wh-pdoc-bp-section">
                <h4><FontAwesomeIcon icon={faNotesMedical} /> Notes</h4>
                <p>{birthPlan.notes}</p>
              </div>
            )}
          </div>
        )}

        {isEditing && (
          <div className="wh-pdoc-bp-form-card">
            <div className="wh-pdoc-card-header">
              <FontAwesomeIcon icon={faClipboardList} />
              <h3>{birthPlan ? 'Edit Birth Plan' : 'Create Your Birth Plan'}</h3>
            </div>
            <div className="wh-pdoc-card-body">
              <div className="wh-pdoc-form-row">
                <div className="wh-pdoc-form-group">
                  <label className="wh-pdoc-label">Delivery Preference</label>
                  <select className="wh-pdoc-select" value={bpForm.delivery_preference} onChange={(e) => setBpForm((f) => ({ ...f, delivery_preference: e.target.value }))}>
                    <option value="">Select preference</option>
                    {bpChoices.delivery_preference.map((c) => <option key={c.value} value={c.value}>{c.display_name}</option>)}
                  </select>
                </div>
                <div className="wh-pdoc-form-group">
                  <label className="wh-pdoc-label">Pain Management</label>
                  <select className="wh-pdoc-select" value={bpForm.pain_management} onChange={(e) => setBpForm((f) => ({ ...f, pain_management: e.target.value }))}>
                    <option value="">Select method</option>
                    {bpChoices.pain_management.map((c) => <option key={c.value} value={c.value}>{c.display_name}</option>)}
                  </select>
                </div>
              </div>

              <div className="wh-pdoc-form-row">
                <div className="wh-pdoc-form-group">
                  <label className="wh-pdoc-label"><FontAwesomeIcon icon={faHospital} /> Hospital Name</label>
                  <input type="text" className="wh-pdoc-input" placeholder="Hospital name" value={bpForm.hospital_name} onChange={(e) => setBpForm((f) => ({ ...f, hospital_name: e.target.value }))} />
                </div>
                <div className="wh-pdoc-form-group">
                  <label className="wh-pdoc-label"><FontAwesomeIcon icon={faUserDoctor} /> Doctor Name</label>
                  <input type="text" className="wh-pdoc-input" placeholder="Doctor name" value={bpForm.doctor_name} onChange={(e) => setBpForm((f) => ({ ...f, doctor_name: e.target.value }))} />
                </div>
              </div>

              <div className="wh-pdoc-form-row">
                <div className="wh-pdoc-form-group">
                  <label className="wh-pdoc-label"><FontAwesomeIcon icon={faPhone} /> Emergency Contact Name</label>
                  <input type="text" className="wh-pdoc-input" placeholder="Contact name" value={bpForm.emergency_contact_name} onChange={(e) => setBpForm((f) => ({ ...f, emergency_contact_name: e.target.value }))} />
                </div>
                <div className="wh-pdoc-form-group">
                  <label className="wh-pdoc-label"><FontAwesomeIcon icon={faPhone} /> Emergency Contact Phone</label>
                  <input type="text" className="wh-pdoc-input" placeholder="Phone number" value={bpForm.emergency_contact_phone} onChange={(e) => setBpForm((f) => ({ ...f, emergency_contact_phone: e.target.value }))} />
                </div>
              </div>

              <div className="wh-pdoc-form-group">
                <label className="wh-pdoc-label"><FontAwesomeIcon icon={faStar} /> Special Requests</label>
                <textarea className="wh-pdoc-textarea" rows={3} placeholder="Any special requests for your delivery..." value={bpForm.special_requests} onChange={(e) => setBpForm((f) => ({ ...f, special_requests: e.target.value }))} />
              </div>

              <div className="wh-pdoc-form-group">
                <label className="wh-pdoc-label"><FontAwesomeIcon icon={faNotesMedical} /> Additional Notes</label>
                <textarea className="wh-pdoc-textarea" rows={2} placeholder="Any additional notes..." value={bpForm.notes} onChange={(e) => setBpForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>

              <div className="wh-pdoc-form-actions">
                {birthPlan && <button className="wh-pdoc-btn wh-pdoc-btn-cancel" onClick={() => setEditingBp(false)}>{t('common:buttons.cancel')}</button>}
                <button className="wh-pdoc-btn wh-pdoc-btn-primary" onClick={handleSaveBirthPlan} disabled={savingBp}>
                  {savingBp ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</> : <><FontAwesomeIcon icon={faCheck} /> {birthPlan ? 'Update Plan' : 'Save Plan'}</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Render: Hospital Bag Tab                                         */
  /* ---------------------------------------------------------------- */

  const renderHospitalBag = () => (
    <div className="wh-pdoc-hospital-bag">
      <div className="wh-pdoc-bag-progress">
        <div className="wh-pdoc-bag-progress-info">
          <span className="wh-pdoc-bag-progress-text">Packing Progress</span>
          <span className="wh-pdoc-bag-progress-count">{checkedCount} / {totalBagItems} items</span>
        </div>
        <div className="wh-pdoc-bag-progress-bar">
          <div className="wh-pdoc-bag-progress-fill" style={{ width: `${totalBagItems > 0 ? (checkedCount / totalBagItems) * 100 : 0}%` }} />
        </div>
      </div>

      <div className="wh-pdoc-bag-columns">
        {bagColumns.map((col) => {
          const { icon, color } = resolveBagStyle(col.title);
          return (
          <div className="wh-pdoc-bag-column" key={col.title}>
            <div className="wh-pdoc-bag-col-header" style={{ borderBottomColor: color }}>
              <div className="wh-pdoc-bag-col-icon" style={{ background: `${color}15`, color }}>
                <FontAwesomeIcon icon={icon} />
              </div>
              <h4>{col.title}</h4>
            </div>
            <div className="wh-pdoc-bag-items">
              {col.items.map((item) => {
                const checked = !!checkedItems[item.item_key];
                return (
                  <button key={item.item_key} className={`wh-pdoc-bag-item ${checked ? 'checked' : ''}`} onClick={() => toggleItem(item.item_key)}>
                    <FontAwesomeIcon icon={checked ? faSquareCheck : faSquare} className="wh-pdoc-bag-checkbox" />
                    <span className={checked ? 'wh-pdoc-bag-item-done' : ''}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );

  /* ---------------------------------------------------------------- */
  /*  Main Render                                                      */
  /* ---------------------------------------------------------------- */

  return (
    <div className="wh-pdoc-page">
      {toast && (
        <div className="wh-pdoc-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      <ConfirmDialog
        open={confirmDeleteDoc !== null}
        title="Delete Document?"
        message="This will permanently remove the document. This action cannot be undone."
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={() => { if (confirmDeleteDoc !== null) handleDeleteDoc(confirmDeleteDoc); }}
        onClose={() => setConfirmDeleteDoc(null)}
      />

      <section className="wh-pdoc-hero">
        <div className="wh-pdoc-hero-circle wh-pdoc-hero-circle-1" />
        <div className="wh-pdoc-hero-circle wh-pdoc-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="wh-pdoc-hero-content">
            <div className="wh-pdoc-hero-badge"><FontAwesomeIcon icon={faFileLines} /> Pregnancy</div>
            <h1 className="wh-pdoc-hero-title">{t('womensHealth:pregnancyDocs.title')}</h1>
            <p className="wh-pdoc-hero-subtitle">
              Keep your pregnancy documents organized, create your birth plan, and prepare your hospital bag.
            </p>
          </div>
        </Container>
      </section>

      <Container>
        <ProfileStrip
          summary={`Week ${pregnancy.current_week ?? '—'} · LMP ${pregnancy.lmp_date ? formatDate(pregnancy.lmp_date) : '—'}`}
          hubPath="/womens-health/pregnancy"
          hubLabel="Pregnancy Dashboard"
        />
        <div className="wh-pdoc-tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`wh-pdoc-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="wh-pdoc-tab-content">
          {activeTab === 'documents' && renderDocuments()}
          {activeTab === 'birth-plan' && renderBirthPlan()}
          {activeTab === 'hospital-bag' && renderHospitalBag()}
        </div>
      </Container>
    </div>
  );
};

export default PregnancyDocs;
